// ──────────────────────────────────────────────────────────────────────────
// StatisticheFiltriContext.js — Context React per filtri condivisi tra
// i sotto-tab della pagina Statistiche del Diario Operativo.
//
// Refactor v131 sub-deploy 3:
//   - 3 filtri: Stagione (stagione_id), Periodo (label), Sistema (sistema_id)
//   - Filtri persistenti tra sotto-tab + persistenza sessionStorage (D118)
//   - Filtro "Tipo" rimosso (decisione utente sub-deploy 3)
// ──────────────────────────────────────────────────────────────────────────

import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

const KEY_STORAGE = "diario_statistiche_filtri_v3";

const DEFAULT_STATE = {
  stagione_id: null,
  periodo: "tutta_stagione",
  da_data_personalizzato: null,
  a_data_personalizzato: null,
  sistema_id: null,
};

function leggiSessione() {
  try {
    const raw = sessionStorage.getItem(KEY_STORAGE);
    if (!raw) return { ...DEFAULT_STATE };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATE, ...parsed };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

const StatisticheFiltriContext = createContext(null);

export function StatisticheFiltriProvider({ children }) {
  const [stato, setStato] = useState(leggiSessione);

  useEffect(() => {
    try { sessionStorage.setItem(KEY_STORAGE, JSON.stringify(stato)); } catch {}
  }, [stato]);

  const setStagione = useCallback((stagioneId) => {
    setStato((s) => ({ ...s, stagione_id: stagioneId }));
  }, []);

  const setPeriodo = useCallback((p, daData = null, aData = null) => {
    setStato((s) => ({
      ...s,
      periodo: p,
      da_data_personalizzato: p === "personalizzato" ? daData : null,
      a_data_personalizzato: p === "personalizzato" ? aData : null,
    }));
  }, []);

  const setDatePersonalizzate = useCallback((daData, aData) => {
    setStato((s) => ({
      ...s,
      da_data_personalizzato: daData,
      a_data_personalizzato: aData,
    }));
  }, []);

  const setSistema = useCallback((sistemaId) => {
    setStato((s) => ({ ...s, sistema_id: sistemaId }));
  }, []);

  const resetTutti = useCallback(() => {
    setStato({ ...DEFAULT_STATE });
  }, []);

  const queryParams = useMemo(() => {
    const params = {};
    if (stato.stagione_id) params.stagione_id = stato.stagione_id;
    if (stato.sistema_id) params.sistema_id = stato.sistema_id;

    const oggi = new Date();
    const fmtIso = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${dd}`;
    };
    const aIso = fmtIso(oggi);

    switch (stato.periodo) {
      case "anno_solare":
        params.da_data = `${oggi.getFullYear()}-01-01`;
        params.a_data = aIso;
        break;
      case "ultimi_12m": {
        const d = new Date(oggi); d.setFullYear(d.getFullYear() - 1);
        params.da_data = fmtIso(d); params.a_data = aIso;
        break;
      }
      case "ultimo_semestre": {
        const d = new Date(oggi); d.setMonth(d.getMonth() - 6);
        params.da_data = fmtIso(d); params.a_data = aIso;
        break;
      }
      case "ultimo_trimestre": {
        const d = new Date(oggi); d.setMonth(d.getMonth() - 3);
        params.da_data = fmtIso(d); params.a_data = aIso;
        break;
      }
      case "ultimo_mese": {
        const d = new Date(oggi); d.setMonth(d.getMonth() - 1);
        params.da_data = fmtIso(d); params.a_data = aIso;
        break;
      }
      case "personalizzato":
        if (stato.da_data_personalizzato) params.da_data = stato.da_data_personalizzato;
        if (stato.a_data_personalizzato) params.a_data = stato.a_data_personalizzato;
        break;
      case "tutta_stagione":
      case "tutto":
      default:
        break;
    }
    return params;
  }, [stato]);

  const hasFiltriAttivi = useMemo(() => {
    return (
      stato.stagione_id !== DEFAULT_STATE.stagione_id ||
      stato.periodo !== DEFAULT_STATE.periodo ||
      stato.sistema_id !== DEFAULT_STATE.sistema_id
    );
  }, [stato]);

  const value = useMemo(() => ({
    ...stato,
    setStagione, setPeriodo, setDatePersonalizzate,
    setSistema, resetTutti,
    queryParams, hasFiltriAttivi,
  }), [stato, setStagione, setPeriodo, setDatePersonalizzate, setSistema, resetTutti, queryParams, hasFiltriAttivi]);

  return (
    <StatisticheFiltriContext.Provider value={value}>
      {children}
    </StatisticheFiltriContext.Provider>
  );
}

export function useStatisticheFiltri() {
  const ctx = useContext(StatisticheFiltriContext);
  if (!ctx) {
    throw new Error("useStatisticheFiltri deve essere usato dentro <StatisticheFiltriProvider>");
  }
  return ctx;
}
